import { useState } from 'react';
import PropTypes from 'prop-types';
import { PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import InputBase from '@/components/core/form/InputBase';
import { unitToString } from '@/utils/unitUtils';

const StaticUnitField = ({ label, className = '' }) => {
  return (
    <div
      className={`input input-bordered input-sm join-item w-full flex items-center bg-base-100 text-base-content ${className}`}
    >
      {label}
    </div>
  );
};

StaticUnitField.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const SavedPropertyRow = ({ property, isDeleting, isUpdating, onDelete, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(property.label);

  const numeratorLabel = unitToString({
    numerator: property?.unit?.numerator ?? [],
    denominator: [],
  });
  const denominatorLabel = property?.unit?.denominator?.length
    ? unitToString({
        numerator: property.unit.denominator,
        denominator: [],
      })
    : '—';

  const startEditing = () => {
    setDraftLabel(property.label);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraftLabel(property.label);
    setIsEditing(false);
  };

  const saveEditing = async () => {
    const trimmed = draftLabel.trim();
    if (!trimmed || trimmed === property.label) {
      cancelEditing();
      return;
    }
    const ok = await onUpdate(property, { label: trimmed });
    if (ok) {
      setIsEditing(false);
    }
  };

  const disabled = isDeleting || isUpdating;

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <InputBase
          value={isEditing ? draftLabel : property.label}
          readOnly={!isEditing}
          disabled={isUpdating}
          onChange={event => setDraftLabel(event.target.value)}
          className="!w-[46%]"
        />
        <StaticUnitField label={numeratorLabel || '—'} className="!w-40" />
        <span className="text-2xl text-gray-400">/</span>
        <StaticUnitField label={denominatorLabel} className="!w-40" />
        {isEditing ? (
          <>
            <button type="button" className="btn btn-sm btn-accent" disabled={isUpdating} onClick={saveEditing}>
              {isUpdating ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-square btn-sm btn-ghost"
              disabled={isUpdating}
              onClick={cancelEditing}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-square btn-sm btn-ghost"
              disabled={disabled}
              onClick={startEditing}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="btn btn-square btn-sm btn-ghost text-error"
              disabled={disabled}
              onClick={onDelete}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

SavedPropertyRow.propTypes = {
  property: PropTypes.object.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default SavedPropertyRow;
